import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { describeFailure } from '@/shared/model/failure';
import { Button } from '@/shared/ui/Button';
import { ErrorState } from '@/shared/ui/States';

/* A failed read, explained from its status rather than from a guess.
 *
 * **Every screen composed this by hand, and that is how a 403 came to be reported as an outage.**
 * `ErrorState` takes a `guidance` string, so each caller wrote one sentence covering every failure —
 * and on the Connections screen that sentence was *"Lumiq could not reach its own API… check that
 * the platform is running"*, shown to an approver whose role simply does not include reading
 * connections. The panel's own technical detail said `read on catalog.connection was denied`, so the
 * page contradicted itself, and the Retry button under it could only ever fail again.
 *
 * This takes the error instead of a sentence, so a screen *cannot* attribute a denial to a network
 * fault: the sentence is chosen by `describeFailure`, and the retry control is rendered only when
 * another attempt could plausibly succeed.
 *
 * `subject` is the screen's own noun phrase — "the source list", "this connection" — because
 * "Access denied for the source list" is what a reader can act on and "Access denied" is not. It is
 * a translated string rather than a key so a caller can pass an interpolated one.
 */

export interface FailurePanelProps {
  readonly error: unknown;
  /** What could not be read, already translated, in the reader's words. */
  readonly subject: string;
  /** The heading. Say what could not be done, never the exception. */
  readonly title: string;
  /** The server's own message, behind a disclosure. Absent when there is nothing useful to show. */
  readonly detail?: string | undefined;
  /** Called by the retry control. **Absent means no control**, whatever the status says. */
  readonly onRetry?: (() => void) | undefined;
}

export function FailurePanel({
  error,
  subject,
  title,
  detail,
  onRetry,
}: FailurePanelProps): ReactElement {
  const { t } = useTranslation();
  const failure = describeFailure(error);

  return (
    <ErrorState
      title={title}
      guidance={t(failure.guidanceKey, { subject })}
      detail={detail}
      detailLabel={t('failure.detailLabel')}
      action={
        /* Both conditions, and the order is not arbitrary. `retryable` is about the *failure* — a
           denial does not become permitted on a second attempt — and `onRetry` is about the
           *screen*, which may have nothing to refetch. Either one absent means no button, because a
           control that cannot work is worse than a missing one. */
        failure.retryable && onRetry !== undefined ? (
          <Button onClick={onRetry}>{t('failure.retry')}</Button>
        ) : undefined
      }
    />
  );
}
